/* FRAMES DASHBOARD — the third view of Frames by Model & Colour (Max 01/11, four charts from her workbook: frame colours by
   year, "Top 3 favorite color of frame in {year}", one model's colours by year, colours stacked by model; "làm 1 trang
   dashboard chung với bảng hoặc tab riêng tùy bạn nhưng hợp lý"). It draws the rows the table holds, under the same
   filters (line, model, frame colour, year), so a chart and the table never disagree — "one model's colours" is the Model
   filter on the first chart.

   Kept readable on purpose: the years take the portal's categorical colours in their fixed order (Max 01/11: one blue in
   five shades could not be told apart — so five distinct hues, at most five years, the latest picked; the palette is the
   validated one, with the legend, the tooltip and the By year table beside it); a pie holds the top three and Other; the
   stack holds the six biggest models and Other — a 30-colour stack can't be read. */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from './ThemeContext';
import { SERIES, OTHER_COLOR, SURFACE } from './explorerModel';

const n0 = (v) => (v ? Math.round(v).toLocaleString('en-US') : '0');
const compact = (v) => { const x = Math.abs(Number(v) || 0); return x >= 1e6 ? `${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `${Math.round(x / 1e3)}K` : `${Math.round(x)}`; };
const TOP_COLOURS = 10;
const TOP_MODELS = 6;
const tipStyle = { background: 'var(--portal-surface)', border: '1px solid var(--portal-border)', color: 'var(--portal-text)', fontSize: 12 };
const axisTick = { fontSize: 11, fill: 'var(--portal-text-muted)' };
const legendText = (v) => <span style={{ color: 'var(--portal-text)' }}>{v}</span>;

export default function ModelColorDashboard({ rows, cols }) {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  const mode = resolved === 'dark' ? 'dark' : 'light';
  const other = t('xModelColor.other');

  const sumOf = (r) => cols.reduce((s, { i }) => s + (r.years[i] || 0), 0);
  const byColour = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.color, (m.get(r.color) || 0) + sumOf(r)));
    return [...m.entries()].map(([color, qty]) => ({ color, qty })).sort((a, b) => b.qty - a.qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols]);
  const byModel = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.model, (m.get(r.model) || 0) + sumOf(r)));
    return [...m.entries()].map(([model, qty]) => ({ model, qty })).sort((a, b) => b.qty - a.qty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, cols]);
  const total = byColour.reduce((s, c) => s + c.qty, 0);
  const topColours = byColour.slice(0, TOP_COLOURS).map((c) => c.color);
  const colourKey = (c) => (topColours.includes(c) ? c : other);
  const xColours = [...topColours, ...(byColour.length > TOP_COLOURS ? [other] : [])];

  /* 1. colours, year by year — the latest five picked years */
  const yearCols = cols.slice(-5);
  const chartYears = useMemo(() => xColours.map((c) => {
    const o = { color: c };
    yearCols.forEach(({ y, i }) => { o[y] = rows.filter((r) => colourKey(r.color) === c).reduce((s, r) => s + (r.years[i] || 0), 0); });
    return o;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, cols, byColour]);

  /* 2. the top three colours over the years picked in the Year filter (Max 01/11: no year box of its own) */
  const pie = useMemo(() => {
    const all = byColour.filter((x) => x.qty).map((x) => ({ name: x.color, value: x.qty }));
    const rest = all.slice(3).reduce((s, x) => s + x.value, 0);
    return [...all.slice(0, 3), ...(rest ? [{ name: other, value: rest }] : [])];
  }, [byColour, other]);
  const pieTotal = pie.reduce((s, x) => s + x.value, 0);

  /* 3. colours stacked by model — the six biggest models and Other */
  const topModels = byModel.slice(0, TOP_MODELS).map((m) => m.model);
  const stackKeys = [...topModels, ...(byModel.length > TOP_MODELS ? [other] : [])];
  const chartStack = useMemo(() => xColours.map((c) => {
    const o = { color: c };
    stackKeys.forEach((k) => { o[k] = 0; });
    rows.filter((r) => colourKey(r.color) === c).forEach((r) => { const k = topModels.includes(r.model) ? r.model : other; o[k] += sumOf(r); });
    return o;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, cols, byColour, byModel]);
  const modelColour = (k, idx) => (k === other ? OTHER_COLOR[mode] : SERIES[mode][idx % SERIES[mode].length]);

  const yrs = cols.map((c) => c.y);
  const last = cols[cols.length - 1]; const prev = cols[cols.length - 2];
  const lastQ = last ? rows.reduce((s, r) => s + (r.years[last.i] || 0), 0) : 0;
  const prevQ = prev ? rows.reduce((s, r) => s + (r.years[prev.i] || 0), 0) : 0;
  const yoy = prevQ ? (lastQ - prevQ) / prevQ : null;

  if (!rows.length) return <p className="sbc-muted">{t('xModelColor.none')}</p>;
  return (
    <div className="mcd">
      <div className="iv-tiles mcd-tiles" data-mcd-kpis>
        <div className="iv-tile"><span>{t('xModelColor.dTotal')}</span><b data-mcd-total>{n0(total)}</b><em>{yrs.length > 1 ? `${yrs[0]} – ${yrs[yrs.length - 1]}` : yrs[0]}</em></div>
        <div className="iv-tile"><span>{t('xModelColor.dTopColour')}</span><b>{byColour[0]?.color || '—'}</b><em>{total ? `${((byColour[0].qty / total) * 100).toFixed(1)}% · ${n0(byColour[0].qty)}` : ''}</em></div>
        <div className="iv-tile"><span>{t('xModelColor.dTopModel')}</span><b>{byModel[0]?.model || '—'}</b><em>{total ? `${((byModel[0].qty / total) * 100).toFixed(1)}% · ${n0(byModel[0].qty)}` : ''}</em></div>
        {last && prev && (
          <div className="iv-tile"><span>{t('xModelColor.dYoy', { a: last.y, b: prev.y })}</span>
            <b className={yoy > 0 ? 'yoy-up' : yoy < 0 ? 'yoy-dn' : ''}>{yoy == null ? '—' : `${yoy > 0 ? '▲ +' : yoy < 0 ? '▼ −' : ''}${Math.abs(yoy * 100).toFixed(1)}%`}</b>
            <em>{n0(lastQ)} vs {n0(prevQ)}{last.y >= new Date(Date.now() + 7 * 3600000).getUTCFullYear() ? ` · ${t('xModelColor.dToDate', { y: last.y })}` : ''}</em></div>
        )}
      </div>

      <section className="yoy-card" aria-labelledby="mcd-a">
        <h3 id="mcd-a">{t('xModelColor.dByYear')}</h3>
        {cols.length > 5 && <p className="sbc-muted mcd-note">{t('xModelColor.dLatestFive', { a: yearCols[0].y, b: yearCols[yearCols.length - 1].y })}</p>}
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartYears} margin={{ top: 10, right: 12, left: 4, bottom: 4 }} barGap={2} barCategoryGap="18%">
            <CartesianGrid vertical={false} stroke="var(--portal-border)" />
            <XAxis dataKey="color" tick={axisTick} tickLine={false} axisLine={{ stroke: 'var(--portal-border)' }} interval={0} height={44} />
            <YAxis tick={axisTick} tickFormatter={compact} width={48} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v, n) => [n0(v), n]} cursor={{ fill: 'rgba(127,127,127,.10)' }} contentStyle={tipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendText} />
            {yearCols.map(({ y }, k) => (
              <Bar key={y} dataKey={String(y)} name={String(y)} fill={SERIES[mode][k]} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </section>

      <div className="mcd-grid">
        <section className="yoy-card" aria-labelledby="mcd-b">
          <h3 id="mcd-b">{t('xModelColor.dTop3', { y: yrs.length > 1 ? `${yrs[0]} – ${yrs[yrs.length - 1]}` : yrs[0] })}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" innerRadius="52%" outerRadius="86%" paddingAngle={0}
                stroke={SURFACE[mode]} strokeWidth={2} isAnimationActive={false}>
                {pie.map((p, i) => <Cell key={p.name} fill={p.name === other ? OTHER_COLOR[mode] : SERIES[mode][i]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${n0(v)} (${pieTotal ? ((v / pieTotal) * 100).toFixed(1) : 0}%)`, n]} contentStyle={tipStyle} />
            </PieChart>
          </ResponsiveContainer>
          {/* the legend and the numbers in one: name, share, quantity — never colour alone */}
          <table className="pa-table mcd-pietable" data-mcd-pie>
            <tbody>
              {pie.map((p, i) => (
                <tr key={p.name}>
                  <td><i className="mcd-sw" style={{ background: p.name === other ? OTHER_COLOR[mode] : SERIES[mode][i] }} />{p.name}</td>
                  <td className="num">{pieTotal ? `${((p.value / pieTotal) * 100).toFixed(1)}%` : ''}</td><td className="num">{n0(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="yoy-card" aria-labelledby="mcd-c">
          <h3 id="mcd-c">{t('xModelColor.dStack')}</h3>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartStack} margin={{ top: 10, right: 12, left: 4, bottom: 4 }} barCategoryGap="22%">
              <CartesianGrid vertical={false} stroke="var(--portal-border)" />
              <XAxis dataKey="color" tick={axisTick} tickLine={false} axisLine={{ stroke: 'var(--portal-border)' }} interval={0} height={44} />
              <YAxis tick={axisTick} tickFormatter={compact} width={48} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v, n) => [n0(v), n]} cursor={{ fill: 'rgba(127,127,127,.10)' }} contentStyle={tipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={legendText} />
              {stackKeys.map((k, idx) => (
                <Bar key={k} dataKey={k} name={k} stackId="m" fill={modelColour(k, idx)} stroke={SURFACE[mode]} strokeWidth={1}
                  maxBarSize={28} isAnimationActive={false} radius={idx === stackKeys.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>
    </div>
  );
}
